import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PinEntry } from "../features/player/PinEntry.js";
import { NicknameEntry } from "../features/player/NicknameEntry.js";
import { WaitingRoom } from "../features/player/WaitingRoom.js";
import { useT } from "../i18n/index.js";

type Step =
  | { kind: "pin" }
  | { kind: "nickname"; pin: string; quizTitle: string; twoStepRequired: boolean }
  | { kind: "waiting"; sessionId: string; playerId: string; playerToken: string; nickname: string };

export function JoinPage() {
  const t = useT();
  const { pin: urlPin } = useParams<{ pin?: string }>();
  const navigate = useNavigate();
  const initialPin = urlPin?.replace(/\D/g, "").slice(0, 6) ?? "";
  const hasValidUrlPin = urlPin ? /^\d{6}$/.test(urlPin) : false;
  const [step, setStep] = useState<Step>(
    hasValidUrlPin
      ? { kind: "nickname", pin: initialPin, quizTitle: "", twoStepRequired: false }
      : { kind: "pin" },
  );

  if (step.kind === "waiting") {
    return (
      <WaitingRoom
        sessionId={step.sessionId}
        playerId={step.playerId}
        playerToken={step.playerToken}
        nickname={step.nickname}
        onGameStarted={() =>
          navigate(`/play/${step.sessionId}`, {
            replace: true,
            state: {
              playerId: step.playerId,
              playerToken: step.playerToken,
              nickname: step.nickname,
            },
          })
        }
      />
    );
  }

  return (
    <main className="join-page">
      <section className="join-card" aria-labelledby="join-title">
        <div className="join-logo">
          <span className="join-logo-mark">HootApp</span>
          <h1 className="join-heading" id="join-title">
            {step.kind === "pin" ? t("landing.joinTitle") : t("join.chooseName")}
          </h1>
          {step.kind === "pin" && (
            <p className="join-subtitle">{t("landing.joinSubtitle")}</p>
          )}
        </div>
        <hr className="join-divider" />
        {step.kind === "pin" ? (
          <PinEntry
            initialPin={initialPin}
            onValidated={(pin, quizTitle, twoStepRequired) =>
              setStep({ kind: "nickname", pin, quizTitle, twoStepRequired })
            }
          />
        ) : (
          <NicknameEntry
            pin={step.pin}
            quizTitle={step.quizTitle}
            twoStepRequired={step.twoStepRequired}
            onJoined={(result) =>
              setStep({
                kind: "waiting",
                sessionId: result.sessionId,
                playerId: result.playerId,
                playerToken: result.playerToken,
                nickname: result.nickname,
              })
            }
          />
        )}
      </section>
    </main>
  );
}
